import type { EObject, Reference } from '../models';
import type { UserTask } from '../models/UserTask';
import { MAX_SEARCH_SIZE_SELECT_OPTIONS, PROCESS_INSTANCE_CLASS_ID, USER_TASK_INTERFACE_ID } from './constants';
import type { BaseParams, BodyParams } from '@sydle/ui-api';
import type { FetchResponse, RestService } from '@sydle/ui-api';
import { buildRequestParams } from '@sydle/ui-api';
import { ProcessDelegationInterceptor } from './delegation/process-delegation-interceptor';

export type GetInAttendanceDraftParams = BaseParams & UserTaskParams;
export type CheckPermAttendTaskParams = BaseParams & UserTaskParams & ProcessInstanceParams;
export type TakeOverParams = BaseParams & UserTaskParams;
export type AttendParams = BaseParams & UserTaskWithIdentifierParams & ProcessInstanceParams;
export type ReturnTaskParams = AttendParams;
export type ListConcludeMethodsParams = AttendParams & Executor;
export type DeleteProcessFromParams = AttendParams & Executor;
export type ConcludeParams = BaseParams &
  ProcessInstanceParams &
  Executor & { method: ConcludeMethod; draftId: string };
export type GetUsersListToTransferParams = BaseParams &
  ProcessInstanceParams &
  UserTaskWithIdentifierParams & { queryString?: string; removeOwnUser?: boolean; maxSize?: number };
export type TransferParams = BaseParams &
  ProcessInstanceParams &
  UserTaskParams &
  UserTaskWithIdentifierParams & { userId: string };
export type CheckPermDeleteParams = BaseParams & ProcessInstanceParams & { userTaskClassIdentifier: string };
export type GetUserTasksParams = BaseParams & { processClassId: string; processId: string; timezoneId: string };
export type CreateDraftInterventionParams = BaseParams & {
  processClassId: string;
  processId: string;
  checkDelegation: boolean;
  delegationUser: any;
};

type UserTaskParams = { userTaskClassId: string; userTaskId: string };
type UserTaskWithIdentifierParams = { userTaskClassIdentifier: string; userTaskId: string };
type ProcessInstanceParams = { processInstanceClassId: string; processInstanceId: string };
type Executor = { executor: Reference };

export type ConcludeMethod = { identifier: string; name: string; type: string; _id: string };
export type ConcludeMethodsResult = { concludeMethods: ConcludeMethod[] };
export type AttendTask = { canAttend: boolean; canTakeOver: boolean; canExecute: boolean; actionLabel?: string };
export type AllowedUsers = { total: number; _usersTransfer: { label: string; value: string }[] };
export type TakeOverResult = { draftId: string; executor: Reference };

export enum ProcessMethods {
  START = '_start',
  ATTENDANCE_DRAFT = '_getInAttendanceDraft',
  CHECK_PERM_ATTEND_TASK = '_checkPermAttendTask',
  TAKE_OVER_TASK = '_takeOverTask',
  ATTEND = '_attend',
  RETURN_TASK = '_returnTask',
  LIST_CONCLUDE_METHODS = '_listConcludeMethods',
  USER_LIST = '_getUserListPerm',
  DELETE = '_deleteProcessFrom',
  TRANSFER = '_transferTask',
  GET_USER_TASKS = '_getUserTasks',
  CREATE_INTERVATION = '_createIntervention',
  CONCLUDE = '_conclude',
}

export class ProcessAPI {
  private restService: RestService;

  constructor(restService: RestService, enableDelegation?: boolean) {
    this.restService = restService;

    if (enableDelegation) {
      this.restService.registerInterceptor(new ProcessDelegationInterceptor());
    }
  }

  start(
    args: BaseParams & BodyParams & { processClassId: string },
  ): Promise<FetchResponse<{ process: EObject; userTasksInAttendance: Reference[] }>> {
    const { processClassId } = args;
    const requestParams = buildRequestParams({
      _classId: processClassId,
      methodIdentifier: ProcessMethods.START,
      requestParams: args.requestParams,
      body: args.body,
    });

    return this.restService.post(requestParams);
  }

  getInAttendanceDraft(args: GetInAttendanceDraftParams): Promise<FetchResponse<{ draftId: string | null }>> {
    const { userTaskClassId, userTaskId } = args;
    const requestParams = buildRequestParams({
      packageIdentifier: '_process',
      classIdentifier: '_userTask',
      methodIdentifier: ProcessMethods.ATTENDANCE_DRAFT,
      body: {
        userTaskClassId,
        userTaskId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  checkPermAttendTask(args: CheckPermAttendTaskParams): Promise<FetchResponse<AttendTask>> {
    const { userTaskClassId, userTaskId, processInstanceClassId, processInstanceId } = args;
    const requestParams = buildRequestParams({
      _classId: PROCESS_INSTANCE_CLASS_ID,
      methodIdentifier: ProcessMethods.CHECK_PERM_ATTEND_TASK,
      body: {
        processInstanceClassId,
        processInstanceId,
        userTaskClassId,
        userTaskId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  attend(args: AttendParams): Promise<FetchResponse<UserTask>> {
    const { userTaskId, userTaskClassIdentifier, processInstanceClassId, processInstanceId } = args;
    const requestParams = buildRequestParams({
      _classId: processInstanceClassId,
      methodIdentifier: `${ProcessMethods.ATTEND}${userTaskClassIdentifier}`,
      body: {
        _id: processInstanceId,
        elementId: userTaskId,
        _checkDelegation: true,
        _delegationUser: null,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  takeOver(args: TakeOverParams): Promise<FetchResponse<TakeOverResult>> {
    const { userTaskId, userTaskClassId } = args;
    const requestParams = buildRequestParams({
      _classId: USER_TASK_INTERFACE_ID,
      methodIdentifier: ProcessMethods.TAKE_OVER_TASK,
      body: {
        userTaskClassId,
        userTaskId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  returnTask(args: ReturnTaskParams): Promise<FetchResponse<void>> {
    const { userTaskId, userTaskClassIdentifier, processInstanceClassId, processInstanceId } = args;
    const requestParams = buildRequestParams({
      _classId: processInstanceClassId,
      methodIdentifier: `${ProcessMethods.RETURN_TASK}${userTaskClassIdentifier}`,
      body: {
        _id: processInstanceId,
        elementId: userTaskId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  listConcludeMethods(args: ListConcludeMethodsParams): Promise<FetchResponse<ConcludeMethodsResult>> {
    const { userTaskId, userTaskClassIdentifier, processInstanceClassId, processInstanceId, executor } = args;
    const bodyAsParam = encodeURIComponent(
      JSON.stringify({
        _id: processInstanceId,
        elementId: userTaskId,
        _checkDelegation: true,
        _delegationUser: executor,
      }),
    );

    const requestParams = buildRequestParams({
      _classId: processInstanceClassId,
      methodIdentifier: `${ProcessMethods.LIST_CONCLUDE_METHODS}${userTaskClassIdentifier}`,
      queryParams: {
        _body: bodyAsParam,
      },
      requestParams: args.requestParams,
    });

    return this.restService.get(requestParams);
  }

  deleteProcessFrom(args: DeleteProcessFromParams): Promise<FetchResponse<void>> {
    const { userTaskId, userTaskClassIdentifier, processInstanceClassId, processInstanceId, executor } = args;
    const requestParams = buildRequestParams({
      _classId: processInstanceClassId,
      methodIdentifier: `${ProcessMethods.DELETE}${userTaskClassIdentifier}`,
      body: {
        _id: processInstanceId,
        elementId: userTaskId,
        _checkDelegation: true,
        _delegationUser: executor,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  conclude(args: ConcludeParams): Promise<
    FetchResponse<{
      process: EObject;
      userTasksInAttendance: Reference[];
    }>
  > {
    const { processInstanceClassId, processInstanceId, method, draftId, executor } = args;
    const requestParams = buildRequestParams({
      _classId: processInstanceClassId,
      methodIdentifier: method.identifier,
      body: {
        _id: processInstanceId,
        _draftId: draftId,
        elementId: method._id,
        _checkDelegation: true,
        _delegationUser: executor,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  transferTask(args: TransferParams): Promise<FetchResponse<void>> {
    const { processInstanceClassId, processInstanceId, userTaskClassIdentifier, userTaskClassId, userTaskId, userId } =
      args;
    const requestParams = buildRequestParams({
      _classId: processInstanceClassId,
      methodIdentifier: `${ProcessMethods.TRANSFER}${userTaskClassIdentifier}`,
      body: {
        userId,
        userTaskClassId,
        userTaskId,
        _id: processInstanceId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  getUsersListToTransfer(args: GetUsersListToTransferParams): Promise<FetchResponse<AllowedUsers>> {
    const {
      processInstanceClassId,
      userTaskId,
      userTaskClassIdentifier,
      processInstanceId,
      maxSize,
      queryString,
      removeOwnUser,
    } = args;
    const requestParams = buildRequestParams({
      _classId: PROCESS_INSTANCE_CLASS_ID,
      methodIdentifier: ProcessMethods.USER_LIST,
      body: {
        classId: processInstanceClassId,
        elementId: userTaskId,
        maxSize: maxSize || MAX_SEARCH_SIZE_SELECT_OPTIONS,
        queryString,
        removeOwnUser: removeOwnUser || false,
        userTaskClassIdentifier,
        _id: processInstanceId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  checkPermDelete(args: CheckPermDeleteParams): Promise<FetchResponse<boolean>> {
    const { processInstanceClassId, processInstanceId, userTaskClassIdentifier } = args;
    const requestParams = buildRequestParams({
      methodIdentifier: '_checkPerm',
      _classId: processInstanceClassId,
      _id: processInstanceId,
      body: {
        method: `${ProcessMethods.DELETE}${userTaskClassIdentifier}`,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  getUserTasks(args: GetUserTasksParams): Promise<FetchResponse<any>> {
    const { processClassId, processId, timezoneId } = args;
    const requestParams = buildRequestParams({
      methodIdentifier: ProcessMethods.GET_USER_TASKS,
      _classId: PROCESS_INSTANCE_CLASS_ID,
      body: {
        classId: processClassId,
        processId,
        timezoneId,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }

  createDraftIntervention(args: CreateDraftInterventionParams): Promise<FetchResponse<any>> {
    const { processClassId, processId, checkDelegation, delegationUser } = args;
    const requestParams = buildRequestParams({
      methodIdentifier: ProcessMethods.CREATE_INTERVATION,
      _classId: processClassId,
      body: {
        _id: processId,
        _checkDelegation: checkDelegation,
        _delegationUser: delegationUser,
      },
      requestParams: args.requestParams,
    });

    return this.restService.post(requestParams);
  }
}
